// Cascade-delete helpers: when an asset (collection, extension, booster, card,
// code…) is removed, these functions strip every dangling reference to it from
// the rest of the database (codes & generators offers, booster targets, user
// profiles, auctions).
//
// They handle BOTH the current offers[] format and the legacy top-level reward
// fields (cash/cards/boosters/collections/discountCollectionId) that older code
// documents still use.

import {
	getFirestore,
	collection,
	getDocs,
	updateDoc,
	deleteDoc,
	deleteField,
} from "firebase/firestore"

const db = getFirestore()

// --- Pure helper (exported for testing) -------------------------------------
// Given a raw code/generator document and the asset ids being removed, returns
// { changed, update } where `update` is the partial object to pass to updateDoc.
export function buildCodeCleanup(data, { cardIds = [], boosterIds = [], collectionIds = [] } = {}) {
	const cardSet = new Set(cardIds)
	const boosterSet = new Set(boosterIds)
	const colSet = new Set(collectionIds)
	const update = {}
	let changed = false

	const filterCards = (arr) => arr.filter((c) => !(c && cardSet.has(c.id)))
	const filterBoosters = (arr) => arr.filter((b) => !(b && boosterSet.has(b.id)))
	const filterCollections = (arr) => arr.filter((cid) => !colSet.has(cid))

	// offers[] (current format)
	if (Array.isArray(data.offers)) {
		let offersChanged = false
		const newOffers = data.offers.map((o) => {
			const no = { ...o }
			if (Array.isArray(no.cards)) {
				const f = filterCards(no.cards)
				if (f.length !== no.cards.length) { no.cards = f; offersChanged = true }
			}
			if (Array.isArray(no.boosters)) {
				const f = filterBoosters(no.boosters)
				if (f.length !== no.boosters.length) { no.boosters = f; offersChanged = true }
			}
			if (Array.isArray(no.collections)) {
				const f = filterCollections(no.collections)
				if (f.length !== no.collections.length) { no.collections = f; offersChanged = true }
			}
			if (no.discountCollectionId && colSet.has(no.discountCollectionId)) {
				no.discountCollectionId = null
				if (no.discountTarget === "collection") no.discountTarget = "all"
				offersChanged = true
			}
			return no
		})
		if (offersChanged) { update.offers = newOffers; changed = true }
	}

	// Legacy top-level arrays
	if (Array.isArray(data.cards)) {
		const f = filterCards(data.cards)
		if (f.length !== data.cards.length) { update.cards = f; changed = true }
	}
	if (Array.isArray(data.boosters)) {
		const f = filterBoosters(data.boosters)
		if (f.length !== data.boosters.length) { update.boosters = f; changed = true }
	}
	if (Array.isArray(data.collections)) {
		const f = filterCollections(data.collections)
		if (f.length !== data.collections.length) { update.collections = f; changed = true }
	}

	// Legacy single fields
	if (data.collection && colSet.has(data.collection)) { update.collection = ""; changed = true }
	if (data.card && cardSet.has(data.card)) { update.card = null; update.cardAmount = 0; changed = true }
	if (data.booster && boosterSet.has(data.booster)) { update.booster = null; update.boosterAmount = 0; changed = true }
	if (data.discountCollectionId && colSet.has(data.discountCollectionId)) {
		update.discountCollectionId = null
		if (data.discountTarget === "collection") update.discountTarget = "all"
		changed = true
	}

	return { changed, update }
}

// --- Firestore cleanups ------------------------------------------------------

// Remove card / booster / collection references from every code and generator.
export async function cleanOffersReferences({ cardIds = [], boosterIds = [], collectionIds = [] } = {}) {
	if (!cardIds.length && !boosterIds.length && !collectionIds.length) return
	const opts = { cardIds, boosterIds, collectionIds }

	for (const colName of ["codes", "codeGenerators"]) {
		const snap = await getDocs(collection(db, colName))
		const writes = []
		snap.forEach((d) => {
			const { changed, update } = buildCodeCleanup(d.data(), opts)
			if (changed) writes.push(updateDoc(d.ref, update))
		})
		await Promise.all(writes)
	}
}

// Remove extension ids from every booster's targetExtensions.
export async function removeExtensionsFromBoosters(extensionIds = []) {
	if (!extensionIds.length) return
	const set = new Set(extensionIds)
	const snap = await getDocs(collection(db, "boosters"))
	const writes = []
	snap.forEach((d) => {
		const data = d.data()
		if (Array.isArray(data.targetExtensions)) {
			const f = data.targetExtensions.filter((e) => !set.has(e))
			if (f.length !== data.targetExtensions.length) writes.push(updateDoc(d.ref, { targetExtensions: f }))
		}
	})
	await Promise.all(writes)
}

// Remove collection ids from boosters' targetCollections / legacy collection.
// `skipBoosterIds` lets the caller ignore boosters that are being deleted anyway.
export async function removeCollectionsFromBoosters(collectionIds = [], skipBoosterIds = []) {
	if (!collectionIds.length) return
	const set = new Set(collectionIds)
	const skip = new Set(skipBoosterIds)
	const snap = await getDocs(collection(db, "boosters"))
	const writes = []
	snap.forEach((d) => {
		if (skip.has(d.id)) return
		const data = d.data()
		const update = {}
		let changed = false
		if (Array.isArray(data.targetCollections)) {
			const f = data.targetCollections.filter((c) => !set.has(c))
			if (f.length !== data.targetCollections.length) { update.targetCollections = f; changed = true }
		}
		if (data.collection && set.has(data.collection)) { update.collection = ""; changed = true }
		if (changed) writes.push(updateDoc(d.ref, update))
	})
	await Promise.all(writes)
}

// Remove code ids from every profile: usage history (codes map) + active discounts.
export async function removeCodesFromProfiles(codeIds = []) {
	if (!codeIds.length) return
	const set = new Set(codeIds)
	const snap = await getDocs(collection(db, "profiles"))
	const writes = []
	snap.forEach((d) => {
		const data = d.data()
		const update = {}
		let changed = false
		if (data.codes) {
			for (const cid of codeIds) {
				if (data.codes[cid]) { update[`codes.${cid}`] = deleteField(); changed = true }
			}
		}
		if (Array.isArray(data.activeDiscounts)) {
			const f = data.activeDiscounts.filter((ad) => !(ad && set.has(ad.codeId)))
			if (f.length !== data.activeDiscounts.length) { update.activeDiscounts = f; changed = true }
		}
		if (changed) writes.push(updateDoc(d.ref, update))
	})
	await Promise.all(writes)
}

// Remove collection-targeted active discounts from profiles (when a collection is deleted).
export async function removeCollectionDiscountsFromProfiles(collectionIds = []) {
	if (!collectionIds.length) return
	const set = new Set(collectionIds)
	const snap = await getDocs(collection(db, "profiles"))
	const writes = []
	snap.forEach((d) => {
		const data = d.data()
		if (Array.isArray(data.activeDiscounts)) {
			const f = data.activeDiscounts.filter((ad) => !(ad && ad.target === "collection" && set.has(ad.collectionId)))
			if (f.length !== data.activeDiscounts.length) writes.push(updateDoc(d.ref, { activeDiscounts: f }))
		}
	})
	await Promise.all(writes)
}

// Delete auctions that reference any of the given cards.
export async function deleteAuctionsForCards(cardIds = []) {
	if (!cardIds.length) return
	const set = new Set(cardIds)
	const snap = await getDocs(collection(db, "auctions"))
	const writes = []
	snap.forEach((d) => {
		const data = d.data()
		if (set.has(data.cardId)) writes.push(deleteDoc(d.ref))
	})
	await Promise.all(writes)
}
